import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BaseHttpService } from '@shared/services/base-http/base-http.service';
import { Observable } from 'rxjs';
import {
  CreditLineDetailsResponse,
  CreditLinesApiResponse,
  CreditLinesListApiResponse,
} from './credit-lines.interfaces';

/**
 * Credit lines service
 */
@Injectable({ providedIn: 'root' })
export class CreditLinesService {
  /**
   * Base HTTP service for API calls
   */
  private readonly baseHttpService = inject(BaseHttpService);

  /**
   * Get credit lines summary
   *
   * @param cnpj - Client CNPJ
   * @returns Observable with credit lines summary
   */
  public getSummary(cnpj: string): Observable<CreditLinesApiResponse> {
    const params = new HttpParams().set('client_cnpj', cnpj);
    return this.baseHttpService.get<CreditLinesApiResponse>('api/v1/credit-lines/summary', params);
  }

  /**
   * Get credit lines list
   *
   * @param cnpj - Client CNPJ
   * @param productType - Optional product type filter
   * @param page - Page number (default: 1)
   * @param pageSize - Page size (default: 20)
   * @param sortBy - Optional sort field
   * @param sortOrder - Optional sort order
   * @returns Observable with credit lines list
   */
  public getCreditLines(
    cnpj: string,
    productType?: string,
    page = 1,
    pageSize = 20,
    sortBy?: string,
    sortOrder?: string,
  ): Observable<CreditLinesListApiResponse> {
    let params = new HttpParams()
      .set('client_cnpj', cnpj)
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (productType) {
      params = params.set('product_type', productType);
    }

    if (sortBy) {
      params = params.set('sort_by', sortBy);
    }

    if (sortOrder) {
      params = params.set('sort_order', sortOrder);
    }

    return this.baseHttpService.get<CreditLinesListApiResponse>('api/v1/credit-lines', params);
  }

  /**
   * Get credit line details by contract ID
   *
   * @param contractId - Contract ID to get details for
   * @param clientCnpj - Client CNPJ for authentication
   *
   * @returns Observable with credit line details
   */
  public getCreditLineDetails(
    contractId: string,
    clientCnpj: string,
  ): Observable<CreditLineDetailsResponse> {
    const params = new HttpParams().set('client_cnpj', clientCnpj);
    return this.baseHttpService.get<CreditLineDetailsResponse>(
      `api/v1/credit-lines/${contractId}`,
      params,
    );
  }
}
